import { Fragment, MutableRefObject, ReactNode, useCallback, useRef, useState } from "react";
// See comment below about Flipper
// import { Flipper } from "react-flip-toolkit";
//Components
import DropdownContainer from "./DropdownContainer";
import { Stack } from "@mui/material";

const ANIMATION_DURATION = 300;
const ANIMATION_DELAY = 200;

type TWithoutOnMouseEntered<T> = Omit<T, "onMouseEnter">;

interface AnimatedDropdownMenuProps<T> {
	ButtonSlot: (props: T) => ReactNode;
	slotProps: TWithoutOnMouseEntered<T>[];
	menuDropdowns: ReactNode[];
	caretClassNames?: string;
	caretPosition?: "start" | "center" | "end";
}

export interface IBasicMenuSlotProps {
	id: string;
	dropdownElement?: ReactNode;
	onMouseEnter: () => void;
	onDisabled?: () => void;
	disabled?: boolean;
}

export const AnimatedDropdownMenu = <T extends IBasicMenuSlotProps>({
	slotProps,
	ButtonSlot,
	menuDropdowns,
	caretClassNames,
	caretPosition,
}: Readonly<AnimatedDropdownMenuProps<T>>) => {
	const [activeIndices, setActiveIndices] = useState<number[]>([]);

	const animatingOutTimeout: MutableRefObject<ReturnType<typeof setTimeout> | null> = useRef<ReturnType<typeof setTimeout> | null>(null);

	const resetDropdownState = useCallback((i?: number) => {
		setActiveIndices(typeof i === "number" ? [i] : []);
		animatingOutTimeout.current = null;
	}, []);

	const onMouseEnterHandler = useCallback(
		(i: number) => {
			if (animatingOutTimeout.current) {
				clearTimeout(animatingOutTimeout.current);
				resetDropdownState(i);
				return;
			}
			if (activeIndices[activeIndices.length - 1] === i) {
				return;
			}
			setActiveIndices((prevState) => [...prevState, i]);
		},
		[activeIndices, resetDropdownState]
	);

	const onMouseLeave = () => {
		animatingOutTimeout.current = setTimeout(resetDropdownState, ANIMATION_DURATION - ANIMATION_DELAY);
	};

	const currentIndex = activeIndices[activeIndices.length - 1];

	const prevIndex = activeIndices.length > 1 && activeIndices[activeIndices.length - 2];

	const direction = currentIndex > Number(prevIndex) ? "right" : "left";

	return (
		/*
		 * Using Flipper causes a couple of issues:
		 *
		 * 1. The fonts change when moving between dropdowns (because of multiple font-family declarations which is not easily fixable). A partial hack for this is to
		 * stop promoting the following transformation via will-change:transform (check DropDownStyledComponents.tsx and InvertedDiv.tsx where that property is being used).
		 * This ensures the desired font is used at the end but some inner steps still use the wrong font.
		 * See "Performance" section at https://www.npmjs.com/package/react-flip-toolkit?activeTab=readme
		 *
		 * 2. When a dropdown menu is hidden for a user, then there is a visible shadow being animated instead of avoiding the animation altogether
		 *
		 * As a result it is advised to not use Flipper for this component until a solution to those problems is found.
		 */
		// <Flipper className="flex items-center h-full" flipKey={currentIndex} spring={{ stiffness: 450, overshootClamping: true }}>
		<Stack direction="row" onMouseLeave={onMouseLeave} className="h-full">
			{slotProps.map((props, index) => {
				return (
					<Fragment key={props.id}>
						{ButtonSlot({
							...props,
							onDisabled: resetDropdownState,
							onMouseEnter: () => onMouseEnterHandler(index),
							dropdownElement: currentIndex === index && (
								<DropdownContainer
									caretPosition={caretPosition}
									caretClassNames={caretClassNames}
									direction={direction}
									duration={ANIMATION_DURATION}
								>
									{menuDropdowns[currentIndex]}
									{typeof prevIndex === "number" && menuDropdowns[prevIndex]}
								</DropdownContainer>
							),
						} as T)}
					</Fragment>
				);
			})}
		</Stack>
		// </Flipper>
	);
};
